export type TaskListDropIndicator = {
    top: number;
    left: number;
};

export type TaskListDropTarget = {
    taskListElement: HTMLElement;
    taskIndex: number;
    taskItemRow: HTMLElement;
    insertAfter: boolean;
    // Denotes that the task items that follow should become children / descendants of the dropped item
    reparentsFollowingTaskItems: boolean;
};

/**
 * Gets the single-line content rect without including the row's nested task subtree.
 */
const getTaskItemLineRect = (row: HTMLElement): DOMRect =>
    (
        row.querySelector<HTMLElement>(
            ':scope > .task-item-main > .task-item-content > [data-node-view-content-react] > p',
        ) ?? row
    ).getBoundingClientRect();

/**
 * Gets the viewport Y of the bottom of a row's own content, which is its chips when it has any.
 */
const getTaskItemContentBottom = (row: HTMLElement): number => {
    const chipsRect = row
        .querySelector<HTMLElement>(':scope > .task-item-main > .task-item-chips')
        ?.getBoundingClientRect();

    return chipsRect?.height ? chipsRect.bottom : getTaskItemLineRect(row).bottom;
};

/**
 * Gets the task-list ancestors that contain a row, ordered from the top-level list to its own list.
 */
const getTaskListAncestors = (row: HTMLElement): HTMLElement[] => {
    const taskListElements: HTMLElement[] = [];
    let taskListElement = row.closest('li')?.parentElement;

    while (taskListElement instanceof HTMLElement) {
        taskListElements.unshift(taskListElement);
        taskListElement = taskListElement.closest('li')?.parentElement;
    }

    return taskListElements;
};

/**
 * Gets the direct task-item child of a list that contains the given row.
 */
const getTaskItemInList = ({
    row,
    taskListElement,
}: {
    row: HTMLElement;
    taskListElement: HTMLElement;
}): HTMLElement | null => {
    let taskItemElement = row.closest('li');

    while (taskItemElement && taskItemElement.parentElement !== taskListElement) {
        taskItemElement = taskItemElement.parentElement?.closest('li') ?? null;
    }

    return taskItemElement;
};

/**
 * Gets an insertion target at a task depth relative to one of the gap's surrounding rows.
 */
const getTaskListDropTargetAtDepth = ({
    row,
    depth,
    insertAfter,
}: {
    row: HTMLElement;
    depth: number;
    insertAfter: boolean;
}): TaskListDropTarget | null => {
    const taskListElement = getTaskListAncestors(row)[depth - 1];

    if (!taskListElement) return null;

    const taskItemElement = getTaskItemInList({ row, taskListElement });

    if (!taskItemElement) return null;

    const taskIndex = Array.from(taskListElement.children).indexOf(taskItemElement);
    const taskItemRow = taskItemElement.querySelector<HTMLElement>(':scope > .task-item-row');

    if (taskIndex === -1 || !taskItemRow) return null;

    return {
        taskListElement,
        taskIndex: insertAfter ? taskIndex + 1 : taskIndex,
        taskItemRow,
        insertAfter,
        reparentsFollowingTaskItems: false,
    };
};

/**
 * Gets the inclusive depth range available at an insertion gap.
 */
const getDropDepthRange = ({
    previousDepth,
    nextDepth,
}: {
    previousDepth: number;
    nextDepth: number;
}): [number, number] => {
    if (!nextDepth) return [1, previousDepth];
    if (!previousDepth) return [nextDepth, nextDepth];

    return [Math.min(previousDepth, nextDepth), Math.max(previousDepth, nextDepth)];
};

/**
 * Gets the insertion target for one available depth in an insertion gap.
 */
const getDropTargetAtDepth = ({
    previousRow,
    nextRow,
    previousDepth,
    depth,
}: {
    previousRow: HTMLElement | undefined;
    nextRow: HTMLElement | undefined;
    previousDepth: number;
    depth: number;
}): TaskListDropTarget | null => {
    if (previousRow && depth <= previousDepth) {
        return getTaskListDropTargetAtDepth({ row: previousRow, depth, insertAfter: true });
    }

    if (nextRow) return getTaskListDropTargetAtDepth({ row: nextRow, depth, insertAfter: false });

    return null;
};

/**
 * Gets the structurally valid insertion targets for the depths traversed by an insertion gap.
 */
const getTaskListDropTargets = ({
    previousRow,
    nextRow,
}: {
    previousRow: HTMLElement | undefined;
    nextRow: HTMLElement | undefined;
}): TaskListDropTarget[] => {
    const previousDepth = previousRow ? getTaskListAncestors(previousRow).length : 0;
    const nextDepth = nextRow ? getTaskListAncestors(nextRow).length : 0;
    const [firstDepth, lastDepth] = getDropDepthRange({ previousDepth, nextDepth });
    const dropTargets: TaskListDropTarget[] = [];

    for (let depth = firstDepth; depth <= lastDepth; depth++) {
        const dropTarget = getDropTargetAtDepth({ previousRow, nextRow, previousDepth, depth });

        if (dropTarget) {
            dropTargets.push({
                ...dropTarget,
                reparentsFollowingTaskItems: !!nextRow && dropTarget.insertAfter && nextDepth > depth,
            });
        }
    }

    return dropTargets;
};

/**
 * Chooses the drop target whose checkbox column is nearest the dragged item's checkbox,
 * with cutovers halfway between adjacent depth columns.
 */
const getNearestDropTargetIndex = ({
    dropTargets,
    draggedCheckboxX,
}: {
    dropTargets: TaskListDropTarget[];
    draggedCheckboxX: number;
}): number => {
    for (let targetIndex = 0; targetIndex < dropTargets.length - 1; targetIndex++) {
        const depthColumnX = dropTargets[targetIndex].taskItemRow.getBoundingClientRect().left;
        const nextDepthColumnX = dropTargets[targetIndex + 1].taskItemRow.getBoundingClientRect().left;

        if (draggedCheckboxX < (depthColumnX + nextDepthColumnX) / 2) return targetIndex;
    }

    return dropTargets.length - 1;
};

/**
 * Chooses one of the insertion gap's valid depths by aligning the dragged item's checkbox
 * with the depth's checkbox column.
 *
 * `excludedTaskListAncestor` removes the task lists contained by one row from the candidate
 * depths, which is how an Option-drag copy is kept out of its own original: the gap after the
 * source's subtree sits below the source's last descendant, so its deeper candidates are lists
 * nested inside the source itself.
 */
export const getTaskListDropTarget = ({
    rows,
    insertionIndex,
    draggedCheckboxX,
    excludedTaskListAncestor,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    draggedCheckboxX: number;
    excludedTaskListAncestor?: HTMLElement;
}): TaskListDropTarget | null => {
    const nextRow = rows[insertionIndex];
    const previousRow = rows[insertionIndex - 1];
    const referenceRow = nextRow ?? previousRow;

    if (!referenceRow) return null;

    const dropTargets = getTaskListDropTargets({ previousRow, nextRow }).filter(
        ({ taskListElement }) => !excludedTaskListAncestor?.contains(taskListElement),
    );

    if (!dropTargets.length) return null;

    return dropTargets[getNearestDropTargetIndex({ dropTargets, draggedCheckboxX })];
};

/**
 * Finds the first row whose midpoint is below the client Y coordinate.
 *
 * Binary search (log₂(N)) - lower-bound search over row midpoint Y positions.
 */
export const getTaskItemInsertionIndex = ({ rows, clientY }: { rows: HTMLElement[]; clientY: number }): number => {
    let startIndex = 0;
    let endIndex = rows.length;

    while (startIndex < endIndex) {
        const middleIndex = Math.floor((startIndex + endIndex) / 2);
        const lineRect = getTaskItemLineRect(rows[middleIndex]);

        if (clientY < lineRect.top + lineRect.height / 2) {
            endIndex = middleIndex;
        } else {
            startIndex = middleIndex + 1;
        }
    }

    return startIndex;
};

/**
 * Gets the rendered scale between the drop target's local CSS pixels and viewport pixels.
 */
const getDropTargetScale = ({
    dropTargetElement,
    dropTargetRect,
}: {
    dropTargetElement: HTMLElement;
    dropTargetRect: DOMRect;
}): number => {
    if (!dropTargetElement.offsetWidth) return 1;

    return dropTargetRect.width / dropTargetElement.offsetWidth || 1;
};

// The dragged preview's checkbox offset from its left edge, in grid units: the task list
// card's content padding-left (24px at the 10px grid - see .TaskListEditorContainer)
const DRAG_PREVIEW_CHECKBOX_INSET_GU = 2.4;

/**
 * Gets the viewport X of the dragged item's checkbox, without measuring the drag preview:
 * the preview renders at the drag source's projected position plus its custom drag offset,
 * and its checkbox sits at a fixed inset from that left edge.
 *
 * Falls back to the pointer position when the drag offsets aren't available.
 */
export const getDraggedCheckboxX = ({
    clientX,
    sourceClientX,
    scaledCustomDragOffsetX,
    dropTargetElement,
    gridSize,
}: {
    clientX: number;
    sourceClientX: number | undefined;
    scaledCustomDragOffsetX: number;
    dropTargetElement: HTMLElement | null;
    gridSize: number;
}): number => {
    if (sourceClientX === undefined || !dropTargetElement) return clientX;

    const dropTargetScale = getDropTargetScale({
        dropTargetElement,
        dropTargetRect: dropTargetElement.getBoundingClientRect(),
    });

    return sourceClientX + scaledCustomDragOffsetX + DRAG_PREVIEW_CHECKBOX_INSET_GU * gridSize * dropTargetScale;
};

/**
 * Gets the viewport Y coordinate at the centre of an insertion gap.
 *
 * The gap is measured between the content on either side of it rather than between the rows'
 * box edges: a task's chips leave more space below its row than the next row's padding leaves
 * above it, which sits the indicator low in the gap and close to the following checkbox.
 *
 * Every drop depth at a gap shares the gap's own edges, so only the surrounding rows are needed:
 * a deeper target's row ends where its list does, and a shallower one's row ends where its last
 * descendant does. At either end of the list there's only one row to measure, so its edge is used.
 */
const getDropIndicatorTop = ({
    previousRow,
    nextRow,
    referenceRow,
}: {
    previousRow: HTMLElement | undefined;
    nextRow: HTMLElement | undefined;
    referenceRow: HTMLElement;
}): number => {
    if (previousRow && nextRow) {
        return (getTaskItemContentBottom(previousRow) + getTaskItemLineRect(nextRow).top) / 2;
    }

    const referenceRect = referenceRow.getBoundingClientRect();

    return nextRow ? referenceRect.top : referenceRect.bottom;
};

/**
 * Gets the line position for the insertion gap at the specified row index.
 */
export const getTaskListDropIndicator = ({
    rows,
    insertionIndex,
    dropTargetElement,
    taskListDropTarget,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    dropTargetElement: HTMLElement | null;
    taskListDropTarget?: TaskListDropTarget | null;
}): TaskListDropIndicator | null => {
    if (!dropTargetElement) return null;

    const nextRow = rows[insertionIndex];
    const previousRow = rows[insertionIndex - 1];
    const referenceRow = nextRow ?? previousRow;

    if (!referenceRow) return null;

    const taskListElement = taskListDropTarget?.taskListElement ?? referenceRow.closest('li')?.parentElement;

    if (!(taskListElement instanceof HTMLElement)) return null;

    const dropTargetRect = dropTargetElement.getBoundingClientRect();
    const dropTargetScale = getDropTargetScale({ dropTargetElement, dropTargetRect });
    const indicatorTop = getDropIndicatorTop({ previousRow, nextRow, referenceRow });

    // The indicator's left edge lines up with the drop level's checkboxes: the drop
    // target's row starts at its checkbox, falling back to the drop list's own edge.
    const alignmentRect = (taskListDropTarget?.taskItemRow ?? taskListElement).getBoundingClientRect();

    return {
        // DOM rects are viewport pixels after the canvas transform, while an absolutely
        // positioned child uses the drop target's unscaled local CSS pixel coordinate space.
        top: (indicatorTop - dropTargetRect.top) / dropTargetScale,
        left: (alignmentRect.left - dropTargetRect.left) / dropTargetScale,
    };
};
